import { useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";

import heroBgImg from "../assets/about-hero.webp";
import logoImg from "../assets/marca/logo-beige.svg";

function ArrowIcon() {
  return (
    <svg
      className="shrink-0"
      width="20"
      height="20"
      viewBox="0 0 14 10"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 5H13M13 5L9 1M13 5L9 9"
        stroke="#F2F1DE"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

interface HeroProps {
  className?: string;
}

export default function AboutHero({ className = "" }: HeroProps) {
  const heroRef = useRef<HTMLElement>(null);

  const { scrollY } = useScroll({
    layoutEffect: false,
  });

  const imageY = useTransform(scrollY, [0, 1500], [0, 260]);
  const contentY = useTransform(scrollY, [0, 1500], [0, -90]);
  const imageScale = useTransform(scrollY, [0, 1500], [1.08, 1.18]);
  const overlayOpacity = useTransform(scrollY, [0, 1200], [0.28, 0.42]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);

    if (element) {
      const navbarOffset = 96;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementTop - navbarOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      id="inicio"
      ref={heroRef}
      className={`relative h-screen h-[100svh] w-full overflow-hidden ${className}`}
      data-name="Hero Section"
    >
      {/* Background */}
      <motion.div
        className="absolute inset-0"
        style={{ y: imageY, scale: imageScale }}
        aria-hidden="true"
        initial={{ scale: 1.14, opacity: 0.85 }}
        animate={{ scale: 1.08, opacity: 1 }}
        transition={{
          duration: 1.4,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <img
          src={heroBgImg}
          alt=""
          className="h-full w-full object-cover"
        />

        <motion.div
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
        />
      </motion.div>

      {/* Content */}
      <motion.div
        className="relative z-10 mx-auto flex h-full w-full max-w-[1920px] px-4 sm:px-6 md:px-8 lg:px-10 xl:px-16"
        style={{ y: contentY }}
      >
        <div className="flex h-full w-full items-center">
          <div className="flex w-full max-w-[667px] flex-col items-start justify-center gap-6 py-20 sm:gap-8 sm:py-24 lg:max-w-[1120px] lg:gap-4 lg:py-0">

            <div className="flex w-full flex-col gap-6 sm:gap-8 lg:gap-6">
              {/* Text */}
              <motion.div
                initial={{ opacity: 0, y: 42 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.95,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.3,
                }}
                className="flex flex-col gap-4 text-[#F2F1DE] lg:gap-3"
              >
                <motion.div
                  className="font-['Excessive_Extra_Condensed'] leading-[0.9] not-italic uppercase"
                  initial={{ opacity: 0, y: 36 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.9,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.35,
                  }}
                >
                  {/* Mobile */}
                  <div className="block md:hidden text-[146px] tracking-[2.92px]">
                    <p className="m-0">NO ES SOLO COMER.</p>
                    <p className="m-0">ES STACY.</p>
                  </div>

                  {/* Tablet */}
                  <div className="hidden md:block lg:hidden text-[200px] tracking-[4px]">
                     <p className="m-0">NO ES SOLO COMER.</p>
                    <p className="m-0">ES STACY.</p>
                  </div>

                  {/* Desktop */}
                  <div className="hidden lg:block text-[224px] tracking-[4.48px]">
                    <p className="m-0">NO ES SOLO COMER.</p>
                    <p className="m-0">ES STACY.</p>
                  </div>
                </motion.div>

                {/* <motion.p
                  className="max-w-[560px] font-['Roboto_Mono',monospace] text-[32px] font-normal leading-[1.1] tracking-[0.64px] text-[#F2F1DE]"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.8,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.5,
                  }}
                >
                  Burgers que juntan. Momentos que quedan.
                </motion.p> */}
              </motion.div>

              {/* Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.65,
                }}
                className="flex w-full flex-col gap-3 sm:flex-row sm:gap-4 md:gap-6"
              >
                {/* {<motion.button
                  whileHover={{
                    scale: 1.03,
                    boxShadow: "0 0 30px rgba(186, 70, 67, 0.6)",
                    y: -2,
                  }}
                  whileTap={{ scale: 0.97 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 20,
                  }}
                  className="flex min-h-[64px] w-full items-center justify-center gap-2.5 rounded-lg bg-[#BA4643] px-6 py-4 sm:w-auto sm:min-w-[240px]"
                >
                  <span className="font-['Roboto_Mono',monospace] text-lg font-medium tracking-[0.02em] text-[#F2F1DE] sm:text-xl">
                    PEDÍ AHORA
                  </span>
                  <ArrowIcon />
                </motion.button>} */}

                <motion.a
                  href="https://wa.me/5492613866837?text=%C2%A1Hola%20Stacy!%20Quiero%20hacer%20un%20pedido.%20%C2%BFMe%20comparten%20el%20men%C3%BA%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(186, 70, 67, 0.5)" }}
                  whileTap={{ scale: 0.95 }}
                  className="flex min-h-[64px] w-full items-center justify-center gap-2.5 rounded-lg bg-[#BA4643] px-6 py-4 sm:w-auto sm:min-w-[240px]"
                >
                  <p className="font-['Roboto_Mono',monospace] text-lg font-medium tracking-[0.02em] text-[#F2F1DE] sm:text-xl">
                    PEDÍ AHORA
                  </p>
                  <ArrowIcon />
                </motion.a>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}